import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Globe, ChevronDown, ChevronUp } from "lucide-react";

const languages = [
    { code: "es", label: "ES", name: "Español", flag: "/assets/spanish.png" },
    { code: "en", label: "EN", name: "English", flag: "/assets/english.png" },
    { code: "de", label: "DE", name: "Deutsch", flag: "/assets/german.png" },
    { code: "tr", label: "TR", name: "Türkçe", flag: "/assets/turkish.png" },
    { code: "fr", label: "FR", name: "Français", flag: "/assets/french.png" },
    { code: "ru", label: "RU", name: "Русский", flag: "/assets/russian.png" },
];

const LanguageSwitcher = ({ variant = "light" }) => {
    const { i18n } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    const currentLang =
        languages.find((l) => l.code === i18n.language) ||
        languages.find((l) => i18n.language?.startsWith(l.code)) ||
        languages.find((l) => l.code === "tr") ||
        languages[0];

    const toggleDropdown = () => setIsOpen((prev) => !prev);

    const handleLanguageChange = (code) => {
        i18n.changeLanguage(code);
        setIsOpen(false);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Define styles based on variant
    const isDark = variant === "dark";
    const buttonClasses = isDark
        ? "flex items-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-md transition-all border border-gray-600 hover:border-gray-500"
        : "flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-md transition-all border border-transparent hover:border-white/30";

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={toggleDropdown}
                className={buttonClasses}
            >
                <Globe className="w-4 h-4" />
                <img
                    src={currentLang.flag}
                    alt={currentLang.code}
                    className="w-5 h-5 rounded-full object-cover"
                />
                <span className="text-sm font-medium">{currentLang.label}</span>
                {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-white text-gray-800 rounded-md shadow-lg overflow-hidden z-50 animate-in fade-in zoom-in-95 duration-100 border border-gray-100">
                    {languages.map((lang) => (
                        <button
                            key={lang.code}
                            onClick={() => handleLanguageChange(lang.code)}
                            className={`flex items-center gap-3 w-full px-4 py-2.5 text-sm hover:bg-gray-100 transition-colors ${currentLang.code === lang.code ? "bg-red-50 text-red-600 font-semibold" : ""
                                }`}
                        >
                            <img
                                src={lang.flag}
                                alt={lang.code}
                                className="w-5 h-5 rounded-full object-cover shadow-sm"
                            />
                            <span className="flex-1 text-left">{lang.label}</span>
                            {currentLang.code === lang.code && <div className="w-1.5 h-1.5 rounded-full bg-red-600" />}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default LanguageSwitcher;
